import { useEffect, useState, Suspense, lazy } from 'react';

// RDS
import Heading from '@ramsey-design-system/heading';
import Link from '@ramsey-design-system/link';

// Components
const QrCode = lazy(() => import('~fpu/shared/components/QrCode'));
import DownloadableImage from '~fpu/modules/FpuDashboard/components/DownloadableImage';

// Utils
import { PageAnalyticsWrapper } from '~fpu/shared/utils/PageAnalyticsWrapper';
import { CohortWithProgress } from '~fpu/types';

// Consts
import {
  DESCRIPTIONS_FOR_BULLETINS_AND_ANNOUNCEMENTS,
  DIGITAL_SLIDES,
  PRINTABLE_POSTERS_AND_FLIERS,
  SOCIAL_MEDIA_IMAGES,
  SOCIAL_POSTS,
  VIDEOS,
  ZOOM_BACKGROUNDS
} from '~fpu/modules/FpuDashboard/config/promoteConstants';

import Container from '~fpu/shared/components/Container';

// Utils
import { PATHS } from '~fpu/config/constants';

import {
  PromoteVideoModal,
  PromoteVideoModalTrigger,
  Video
} from './PromoteVideoModal';
import { formatTime } from '~fpu/shared/utils/date-utils';

interface PromoteContentProps {
  data?: CohortWithProgress;
}

const { VIRTUAL_CLASS_POSTS, IN_PERSON_CLASS_POSTS } = SOCIAL_POSTS.SECTIONS;

function SocialMediaImages() {
  return (
    <div>
      <Heading level="3" className="mb-2">
        Social Media Images
      </Heading>

      <div className="gap-lg grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4">
        {SOCIAL_MEDIA_IMAGES.map((image, index) => (
          <DownloadableImage
            key={`social-media-image-${index}`}
            image={image}
            descriptionPrefix="Financial Peace Social Media Post"
          />
        ))}
      </div>
    </div>
  );
}

function DigitalSlides() {
  return (
    <div>
      <Heading level="3" className="mb-2">
        Digital Slides
      </Heading>

      <div className="gap-lg grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4">
        {DIGITAL_SLIDES.map((image, index) => (
          <DownloadableImage
            key={`digital-slide-${index}`}
            image={image}
            descriptionPrefix="Presentation Slide"
          />
        ))}
      </div>
    </div>
  );
}

function PrintableMedia() {
  return (
    <div>
      <Heading level="3" className="mb-2">
        Printable Posters and Fliers
      </Heading>

      <div className="gap-lg grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4">
        {PRINTABLE_POSTERS_AND_FLIERS.map((image, index) => (
          <DownloadableImage
            key={`printable-poster-flier-${index}`}
            image={image}
            descriptionPrefix="Presentation Slide"
          />
        ))}
      </div>
    </div>
  );
}

function Videos() {
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const [selectedPlayerId, setSelectedPlayerId] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleTrigger = (video: Video, playerId: number) => {
    setSelectedVideo(video);
    setSelectedPlayerId(playerId);
    setIsModalOpen(true);
  };

  return (
    <div>
      <Heading level="3" className="mb-2">
        Videos
      </Heading>

      <div className="gap-lg grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4">
        {VIDEOS.map((video, index) => (
          <PromoteVideoModalTrigger
            key={`promote-video-trigger-${index}`}
            video={video}
            playerId={index}
            onTrigger={handleTrigger}
          />
        ))}
      </div>

      <PromoteVideoModal
        video={selectedVideo}
        playerId={selectedPlayerId}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
}

function PostsForVirtualClasses({
  data,
  posts
}: {
  data?: CohortWithProgress;
  posts: string[];
}) {
  if (data?.metadata?.format === 'virtual' || data === undefined) {
    return (
      <div>
        <Heading level="4" className="text-4 mb-2">
          VIRTUAL CLASS POSTS
        </Heading>
        <ul className="mb-3 list-disc pl-2">
          {posts.map((post: string, index: number) => (
            <li
              className="mb-1"
              key={`virtual-class-post-${index}`}
              dangerouslySetInnerHTML={{ __html: post }}
            />
          ))}
        </ul>
      </div>
    );
  }
  return null;
}

function PostsForInPersonClasses({
  data,
  posts
}: {
  data?: CohortWithProgress;
  posts: string[];
}) {
  if (data?.metadata?.format !== 'virtual' || data === undefined) {
    return (
      <div>
        <Heading level="4" className="text-4 mb-2">
          IN-PERSON CLASS POSTS
        </Heading>
        <ul className="mb-3 list-disc pl-2">
          {posts.map((post, index) => (
            <li
              className="mb-1"
              key={`in-person-class-post-${index}`}
              dangerouslySetInnerHTML={{ __html: post }}
            />
          ))}
        </ul>
      </div>
    );
  }
  return null;
}

function ZoomBackgrounds({ data }: PromoteContentProps) {
  if (data?.metadata?.format === 'virtual' || data === undefined) {
    return (
      <div>
        <Heading level="3" className="mb-2">
          Zoom Backgrounds
        </Heading>
        <div className="gap-lg grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4">
          {ZOOM_BACKGROUNDS.map((image, index) => (
            <DownloadableImage
              key={`zoom-bg-${index}`}
              image={image}
              descriptionPrefix="Financial Peace Zoom Background"
            />
          ))}
        </div>
      </div>
    );
  }
  return null;
}

function ClassQRCode({ data }: PromoteContentProps) {
  return data ? (
    <div>
      <Heading level="3" className="mb-3">
        Class Registration QR Code
      </Heading>
      <Suspense fallback={null}>
        <QrCode
          className="mb-4"
          url={`https://www.fpu.com/${data.class_code}`}
          elementId="Promote"
          download
        />
      </Suspense>
    </div>
  ) : null;
}

function Bulletins({ descriptions }: { descriptions: string[] }) {
  return (
    <div>
      <Heading level="3" className="mb-2">
        Descriptions for Bulletins and Announcements
      </Heading>

      <ul className="mb-3 list-disc pl-2">
        {descriptions.map((post, index) => (
          <li
            className="mb-1"
            key={`descriptions-bulletins-announcements-${index}`}
            dangerouslySetInnerHTML={{ __html: post }}
          />
        ))}
      </ul>
    </div>
  );
}

function PromoteContent({
  data,
  shareClassInfo = true
}: PromoteContentProps & { shareClassInfo?: boolean }) {
  const [virtualClassPosts, setVirtualClassPosts] =
    useState<string[]>(VIRTUAL_CLASS_POSTS);
  const [inPersonClassPosts, setInPersonClassPosts] = useState<string[]>(
    IN_PERSON_CLASS_POSTS
  );
  const [bulletinDescriptions, setBulletinDescriptions] = useState<string[]>(
    DESCRIPTIONS_FOR_BULLETINS_AND_ANNOUNCEMENTS
  );

  useEffect(() => {
    if (data) {
      const formattedDate = new Intl.DateTimeFormat('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }).format(new Date(data?.starts_at));

      let classLink = `https://www.fpu.com/${data.class_code}`;
      if (!shareClassInfo) {
        classLink = '[ENTER LINK HERE]';
      }

      const virtualPostsWithLinks = VIRTUAL_CLASS_POSTS.map((post) =>
        post
          .replace(/{{unique registration link}}/g, classLink)
          .replace(/{{date}}/g, formattedDate)
      );

      const inPersonPostsWithLinks = IN_PERSON_CLASS_POSTS.map((post) =>
        post
          .replace(/{{unique registration link}}/g, classLink)
          .replace(/{{date}}/g, formattedDate)
          .replace(/{{venue}}/g, data.venue?.street ?? 'TBA')
          .replace(/{{time}}/g, formatTime(new Date(data.starts_at)))
      );

      const bulletinDescriptionsWithLinks =
        DESCRIPTIONS_FOR_BULLETINS_AND_ANNOUNCEMENTS.map((post) =>
          post
            .replace(/{{unique registration link}}/g, classLink)
            .replace(/{{date}}/g, formattedDate)
        );

      setVirtualClassPosts(virtualPostsWithLinks);
      setInPersonClassPosts(inPersonPostsWithLinks);
      setBulletinDescriptions(bulletinDescriptionsWithLinks);
    }
  }, [data, shareClassInfo]);

  return (
    <div className="overflow-x-hidden">
      <PageAnalyticsWrapper
        category="Coordinator"
        page="Dashboard My Class - Promote"
      >
        <Container>
          <Heading
            level="2"
            size="medium"
            className="text-blue-80 mb-2"
            data-testid="class-message"
          >
            Time to fill up your FPU class!
          </Heading>

          <div className="gap-lg leading-lg mx-auto mb-2 grid w-full grid-cols-1 rounded bg-white p-2 pt-4 sm:p-3 xl:p-4">
            <div>
              <p className="mb-2">
                You&apos;ve signed up to lead{' '}
                <em>Financial Peace University</em> . . . now what? It&apos;s
                time to reach the right people so life-change can begin!
                We&apos;ve got everything you need—from social media posts to
                videos—to help you spread the word about your class.
                <br />
                <br />
                If you&apos;d like to share these resources with your team, go
                right ahead! You can share this link:
                <Link className="ml-0.5" href={PATHS.COORDINATOR.PUBLIC_PROMO}>
                  fpu.com/promo
                </Link>
              </p>
            </div>

            <SocialMediaImages />

            <Heading level="3" className="text-6 mb-1">
              Social Posts
            </Heading>

            <PostsForVirtualClasses data={data} posts={virtualClassPosts} />

            <PostsForInPersonClasses data={data} posts={inPersonClassPosts} />

            <Videos />

            <ZoomBackgrounds data={data} />

            <DigitalSlides />

            <PrintableMedia />

            {shareClassInfo && <ClassQRCode data={data} />}

            <Bulletins descriptions={bulletinDescriptions} />
          </div>
        </Container>
      </PageAnalyticsWrapper>
    </div>
  );
}

export default PromoteContent;
